import { useCallback, useState } from 'react';

/**
 * Cargar datos sin tragarse el fallo.
 *
 * Catorce pantallas tenían el mismo `try/catch/finally`, y las catorce
 * terminaban el `catch` con un `console.error`. El efecto era que un servidor
 * caído, un 500 o un corte de red producían **exactamente la misma pantalla**
 * que tener la cuenta vacía: «no tienes tiendas conectadas», «todavía no tienes
 * pedidos», ceros en el panel. Con una tienda conectada y funcionando, la
 * página de Tiendas te invitaba a conectar la primera.
 *
 * Este es el mismo error que el proyecto ya corrigió una vez para las fuentes
 * de búsqueda —una lista vacía hacía que un raspador bloqueado se viera igual
 * que «no hay resultados»— y que no se había aplicado al resto.
 *
 * Añadir un aviso en cada uno de los catorce sitios habría sido parchear: el
 * siguiente que alguien escriba vuelve a empezar. Aquí el estado de error es
 * parte de la carga, así que **una pantalla no puede enseñar su estado vacío
 * sin haber cargado bien**: `error` y `vacío` son ramas distintas y hay que
 * decidir las dos.
 *
 * ```jsx
 * const { cargando, error, ejecutar, reintentar } = useCarga();
 *
 * useEffect(() => { ejecutar(() => api.getAll().then(r => setCosas(r.data.data.cosas))); }, [ejecutar]);
 *
 * if (cargando) return <Esqueleto />;
 * if (error) return <ErrorAlCargar error={error} onReintentar={reintentar} />;
 * if (cosas.length === 0) return <Vacio />;
 * ```
 */
export function useCarga({ iniciaCargando = true } = {}) {
    const [cargando, setCargando] = useState(iniciaCargando);
    const [error, setError] = useState(null);
    // Se recuerda la última carga para que «Reintentar» repita exactamente esa.
    const [ultima, setUltima] = useState(null);

    const ejecutar = useCallback(async (tarea) => {
        setUltima(() => tarea);
        setCargando(true);
        setError(null);
        try {
            return await tarea();
        } catch (e) {
            setError(e);
            return undefined;
        } finally {
            setCargando(false);
        }
    }, []);

    const reintentar = useCallback(() => {
        if (ultima) ejecutar(ultima);
    }, [ultima, ejecutar]);

    return { cargando, error, ejecutar, reintentar };
}

export default useCarga;
